/**
 * Room 3D Viewer Modal — Embedded Three.js room preview for the booking page.
 * Opens as a full-screen overlay when "View in 3D" is clicked in the photo gallery.
 * Uses the same Three.js room builder as the Metaverse Viewer.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  X, RotateCcw, Bed, Users, Maximize, Minimize, Box,
  Wifi, Wind, Tv, Coffee, Bath, ArrowLeft, ArrowRight,
  ShoppingBag
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { BACKEND_URL } from '@/lib/backendUrl';

const ROOM_CONFIGS = [
  {
    id: 'standard', name: 'Standard Room',
    size: { w: 6, h: 3.2, d: 5 }, price: 150, maxGuests: 2,
    bedType: 'Queen', sqft: 320, bedW: 1.8,
    amenities: ['WiFi', 'AC', 'TV', 'Mini Bar'],
    colors: { wall: '#e8e0d4', floor: '#8B7355', accent: '#2B6CB0', ceiling: '#f5f0e8' },
  },
  {
    id: 'deluxe', name: 'Deluxe Room',
    size: { w: 7.5, h: 3.4, d: 6 }, price: 280, maxGuests: 2,
    bedType: 'King', sqft: 480, bedW: 2.2,
    amenities: ['WiFi', 'AC', 'TV', 'Mini Bar', 'Balcony', 'Bathrobe'],
    colors: { wall: '#ddd5c8', floor: '#6B5B45', accent: '#B08D57', ceiling: '#f0ebe3' },
  },
  {
    id: 'suite', name: 'Presidential Suite',
    size: { w: 10, h: 3.6, d: 8 }, price: 650, maxGuests: 4,
    bedType: 'King + Sofa', sqft: 860, bedW: 2.2,
    amenities: ['WiFi', 'AC', 'TV', 'Mini Bar', 'Balcony', 'Bathrobe', 'Jacuzzi', 'Living Room'],
    colors: { wall: '#d4cec4', floor: '#4A3F35', accent: '#F7931A', ceiling: '#ece7df' },
  },
];

const AMENITY_ICONS = { WiFi: Wifi, AC: Wind, TV: Tv, 'Mini Bar': Coffee, Bathrobe: Bath };

function buildRoom(scene, config) {
  while (scene.children.length > 0) scene.remove(scene.children[0]);

  const { w, h, d } = config.size;
  const { wall, floor, ceiling, accent } = config.colors;

  // Lighting
  scene.add(new THREE.AmbientLight(0xffffff, 0.45));
  const dirLight = new THREE.DirectionalLight(0xffffff, 0.6);
  dirLight.position.set(5, 8, 3);
  dirLight.castShadow = true;
  scene.add(dirLight);

  // Floor
  const floorMesh = new THREE.Mesh(
    new THREE.PlaneGeometry(w, d),
    new THREE.MeshStandardMaterial({ color: floor, roughness: 0.6 })
  );
  floorMesh.rotation.x = -Math.PI / 2;
  floorMesh.receiveShadow = true;
  scene.add(floorMesh);

  // Ceiling
  const ceilMesh = new THREE.Mesh(
    new THREE.PlaneGeometry(w, d),
    new THREE.MeshStandardMaterial({ color: ceiling, roughness: 0.9 })
  );
  ceilMesh.rotation.x = Math.PI / 2;
  ceilMesh.position.y = h;
  scene.add(ceilMesh);

  // Walls
  const wallMat = new THREE.MeshStandardMaterial({ color: wall, roughness: 0.8 });
  const backWall = new THREE.Mesh(new THREE.PlaneGeometry(w, h), wallMat);
  backWall.position.set(0, h / 2, -d / 2);
  scene.add(backWall);

  const leftWall = new THREE.Mesh(new THREE.PlaneGeometry(d, h), wallMat.clone());
  leftWall.position.set(-w / 2, h / 2, 0);
  leftWall.rotation.y = Math.PI / 2;
  scene.add(leftWall);

  const rightWall = new THREE.Mesh(new THREE.PlaneGeometry(d, h), wallMat.clone());
  rightWall.position.set(w / 2, h / 2, 0);
  rightWall.rotation.y = -Math.PI / 2;
  scene.add(rightWall);

  // Accent strip
  const accentStrip = new THREE.Mesh(
    new THREE.PlaneGeometry(w * 0.6, h * 0.4),
    new THREE.MeshStandardMaterial({ color: accent, roughness: 0.5, transparent: true, opacity: 0.18 })
  );
  accentStrip.position.set(0, h * 0.55, -d / 2 + 0.01);
  scene.add(accentStrip);

  // Window
  const winGroup = new THREE.Group();
  winGroup.position.set(w / 2 - 0.01, h * 0.55, -d * 0.15);
  winGroup.rotation.y = -Math.PI / 2;
  winGroup.add(new THREE.Mesh(
    new THREE.PlaneGeometry(2.2, 1.8),
    new THREE.MeshStandardMaterial({ color: 0x87ceeb, emissive: 0x87ceeb, emissiveIntensity: 0.3, roughness: 0.1 })
  ));
  const frameMat = new THREE.MeshStandardMaterial({ color: 0x8b7355 });
  [0.9, -0.9].forEach(y => {
    const fh = new THREE.Mesh(new THREE.BoxGeometry(2.3, 0.06, 0.03), frameMat);
    fh.position.set(0, y, 0.01);
    winGroup.add(fh);
  });
  [-1.1, 0, 1.1].forEach(x => {
    const fv = new THREE.Mesh(new THREE.BoxGeometry(x === 0 ? 0.04 : 0.06, 1.8, 0.03), frameMat);
    fv.position.set(x, 0, 0.01);
    winGroup.add(fv);
  });
  const winLight = new THREE.PointLight(0xfff5e1, 0.8, 6);
  winLight.position.set(0, 0, 0.5);
  winGroup.add(winLight);
  scene.add(winGroup);

  // Bed
  const bw = config.bedW;
  const bedGroup = new THREE.Group();
  bedGroup.position.set(0, 0, -d / 2 + 1.5);
  const darkWood = new THREE.MeshStandardMaterial({ color: 0x3d2b1f, roughness: 0.7 });
  const darkerWood = new THREE.MeshStandardMaterial({ color: 0x2d1f14, roughness: 0.6 });

  const frame = new THREE.Mesh(new THREE.BoxGeometry(bw, 0.5, 2.4), darkWood);
  frame.position.y = 0.25;
  frame.castShadow = true;
  bedGroup.add(frame);

  const mattress = new THREE.Mesh(
    new THREE.BoxGeometry(bw - 0.1, 0.25, 2.3),
    new THREE.MeshStandardMaterial({ color: 0xf5f5f0, roughness: 0.9 })
  );
  mattress.position.y = 0.62;
  bedGroup.add(mattress);

  const duvet = new THREE.Mesh(
    new THREE.BoxGeometry(bw - 0.15, 0.08, 1.4),
    new THREE.MeshStandardMaterial({ color: accent, roughness: 0.8, transparent: true, opacity: 0.9 })
  );
  duvet.position.set(0, 0.78, 0.15);
  bedGroup.add(duvet);

  const pillowMat = new THREE.MeshStandardMaterial({ color: 0xfffff0, roughness: 0.95 });
  [-0.4, 0.4].forEach(x => {
    const p = new THREE.Mesh(new THREE.BoxGeometry(0.55, 0.12, 0.4), pillowMat);
    p.position.set(x, 0.82, -0.8);
    bedGroup.add(p);
  });

  const headboard = new THREE.Mesh(new THREE.BoxGeometry(bw + 0.2, 1.2, 0.08), darkerWood);
  headboard.position.set(0, 1.1, -1.16);
  bedGroup.add(headboard);

  // Nightstands + lamps
  [-bw / 2 - 0.45, bw / 2 + 0.45].forEach(x => {
    const ns = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.55, 0.45), darkWood);
    ns.position.set(x, 0.275, -0.9);
    ns.castShadow = true;
    bedGroup.add(ns);
    const lampBase = new THREE.Mesh(
      new THREE.BoxGeometry(0.12, 0.3, 0.12),
      new THREE.MeshStandardMaterial({ color: accent, roughness: 0.4, metalness: 0.3 })
    );
    lampBase.position.set(x, 0.7, -0.9);
    bedGroup.add(lampBase);
    const shade = new THREE.Mesh(
      new THREE.BoxGeometry(0.28, 0.04, 0.28),
      new THREE.MeshStandardMaterial({ color: 0xfff8e7, roughness: 0.9 })
    );
    shade.position.set(x, 0.9, -0.9);
    bedGroup.add(shade);
    const lampLight = new THREE.PointLight(0xffe4b5, 0.3, 2);
    lampLight.position.set(x, 0.85, -0.9);
    bedGroup.add(lampLight);
  });
  scene.add(bedGroup);

  // Desk
  const deskGroup = new THREE.Group();
  deskGroup.position.set(w / 2 - 0.8, 0, d / 2 - 1);
  const deskTop = new THREE.Mesh(
    new THREE.BoxGeometry(1.2, 0.04, 0.6),
    new THREE.MeshStandardMaterial({ color: 0x5c4033, roughness: 0.5 })
  );
  deskTop.position.y = 0.75;
  deskTop.castShadow = true;
  deskGroup.add(deskTop);
  [[-0.55, -0.25], [0.55, -0.25], [-0.55, 0.25], [0.55, 0.25]].forEach(([x, z]) => {
    const leg = new THREE.Mesh(new THREE.BoxGeometry(0.04, 0.75, 0.04), darkWood);
    leg.position.set(x, 0.375, z);
    deskGroup.add(leg);
  });
  const chairSeat = new THREE.Mesh(new THREE.BoxGeometry(0.45, 0.04, 0.45), darkerWood);
  chairSeat.position.set(0, 0.45, 0.55);
  deskGroup.add(chairSeat);
  const chairBack = new THREE.Mesh(new THREE.BoxGeometry(0.45, 0.5, 0.04), darkerWood);
  chairBack.position.set(0, 0.7, 0.76);
  deskGroup.add(chairBack);
  scene.add(deskGroup);

  // Wardrobe
  const wardrobe = new THREE.Mesh(new THREE.BoxGeometry(0.6, 2.2, 0.5), darkWood);
  wardrobe.position.set(-w / 2 + 0.35, 1.1, d / 2 - 0.6);
  wardrobe.castShadow = true;
  scene.add(wardrobe);

  // TV
  const tvGroup = new THREE.Group();
  tvGroup.position.set(-w / 2 + 0.06, 1.3, -d * 0.15);
  tvGroup.rotation.y = Math.PI / 2;
  tvGroup.add(new THREE.Mesh(
    new THREE.BoxGeometry(1.2, 0.7, 0.04),
    new THREE.MeshStandardMaterial({ color: 0x111111, roughness: 0.3 })
  ));
  tvGroup.add((() => {
    const s = new THREE.Mesh(
      new THREE.PlaneGeometry(1.15, 0.65),
      new THREE.MeshStandardMaterial({ color: 0x1a1a2e, emissive: 0x1a1a2e, emissiveIntensity: 0.05 })
    );
    s.position.z = 0.025;
    return s;
  })());
  scene.add(tvGroup);

  // Bathroom door
  const doorGroup = new THREE.Group();
  doorGroup.position.set(-w / 2 + 0.02, 0, -d / 2 + 1.2);
  doorGroup.rotation.y = Math.PI / 2;
  const door = new THREE.Mesh(
    new THREE.BoxGeometry(0.9, 2.2, 0.08),
    new THREE.MeshStandardMaterial({ color: 0xf5f0e8, roughness: 0.7 })
  );
  door.position.y = 1.1;
  doorGroup.add(door);
  scene.add(doorGroup);

  // Suite living area
  if (config.id === 'suite') {
    const sofaGroup = new THREE.Group();
    sofaGroup.position.set(w / 4, 0, d / 4);
    const sofaSeat = new THREE.Mesh(
      new THREE.BoxGeometry(2, 0.45, 0.8),
      new THREE.MeshStandardMaterial({ color: 0x4a3f35, roughness: 0.8 })
    );
    sofaSeat.position.y = 0.225;
    sofaSeat.castShadow = true;
    sofaGroup.add(sofaSeat);
    const sofaBack = new THREE.Mesh(
      new THREE.BoxGeometry(2, 0.4, 0.12),
      new THREE.MeshStandardMaterial({ color: 0x3d3225, roughness: 0.8 })
    );
    sofaBack.position.set(0, 0.55, -0.34);
    sofaGroup.add(sofaBack);
    const cushion = new THREE.Mesh(
      new THREE.BoxGeometry(0.45, 0.35, 0.1),
      new THREE.MeshStandardMaterial({ color: accent, transparent: true, opacity: 0.8 })
    );
    cushion.position.set(-0.6, 0.6, -0.25);
    sofaGroup.add(cushion);
    const cushion2 = cushion.clone();
    cushion2.position.x = 0.6;
    sofaGroup.add(cushion2);
    const cTable = new THREE.Mesh(
      new THREE.BoxGeometry(0.8, 0.03, 0.5),
      new THREE.MeshStandardMaterial({ color: 0x5c4033, roughness: 0.4 })
    );
    cTable.position.set(0, 0.4, 0.7);
    sofaGroup.add(cTable);
    scene.add(sofaGroup);
  }
}

// Match room type name to a 3D config
function matchRoomConfig(roomTypeName) {
  if (!roomTypeName) return ROOM_CONFIGS[0];
  const lower = roomTypeName.toLowerCase();
  if (lower.includes('suite') || lower.includes('presidential')) return ROOM_CONFIGS[2];
  if (lower.includes('deluxe') || lower.includes('premium') || lower.includes('superior')) return ROOM_CONFIGS[1];
  return ROOM_CONFIGS[0];
}

export function Room3DViewerModal({ open, onClose, roomTypeName, hotelId, onBook }) {
  const containerRef = useRef(null);
  const cleanupRef = useRef(null);
  const statusSpriteRef = useRef(null);
  const [selectedIdx, setSelectedIdx] = useState(() => {
    const matched = matchRoomConfig(roomTypeName);
    return ROOM_CONFIGS.findIndex(c => c.id === matched.id);
  });
  const [aiConfig, setAiConfig] = useState(null);
  const [liveAvailability, setLiveAvailability] = useState(null);
  const [bounties, setBounties] = useState([]);

  const config = ROOM_CONFIGS[selectedIdx];

  // Room name mapping: 3D config id -> hotel room names
  const ROOM_NAME_MAP = {
    standard: ['standard', 'queen', 'standard queen'],
    deluxe: ['deluxe', 'king', 'deluxe king'],
    suite: ['suite', 'presidential', 'executive', 'presidential suite', 'executive suite'],
  };

  // Compute availability for current room type
  const availability = (() => {
    if (!liveAvailability) return null;
    const rooms = liveAvailability.room_grid || [];
    const keywords = ROOM_NAME_MAP[config.id] || [config.id];
    const matched = rooms.filter(r => {
      const name = (r.name || '').toLowerCase();
      return keywords.some(k => name.includes(k));
    });
    const available = matched.filter(r => r.status === 'available').length;
    const occupied = matched.filter(r => r.status === 'occupied').length;
    return { available, occupied, total: matched.length };
  })();

  // Fetch live availability from Marina dashboard
  useEffect(() => {
    if (!open || !hotelId) return;
    let cancelled = false;
    const fetchAvailability = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/marina/${hotelId}/dashboard`);
        const data = await res.json();
        if (!cancelled) setLiveAvailability(data);
      } catch { /* ignore */ }
    };
    fetchAvailability();
    const iv = setInterval(fetchAvailability, 30000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [open, hotelId]);

  // Fetch active bounties for this room
  useEffect(() => {
    if (!open || !hotelId) return;
    let cancelled = false;
    const fetchBounties = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/twin/bounties/${hotelId}?status=open`);
        const data = await res.json();
        if (!cancelled) setBounties(data.bounties || []);
      } catch { /* ignore */ }
    };
    fetchBounties();
    const iv = setInterval(fetchBounties, 30000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [open, hotelId]);

  // Fetch AI config if available
  useEffect(() => {
    if (!open || !hotelId) return;
    fetch(`${BACKEND_URL}/api/metaverse/${hotelId}/room-configs`)
      .then(r => r.json())
      .then(data => {
        if (data.configs && data.configs.length > 0) {
          // Find matching config for current room type
          const match = data.configs.find(c =>
            c.room_type?.toLowerCase().includes(config.id) ||
            config.name.toLowerCase().includes(c.room_type?.toLowerCase() || '')
          );
          setAiConfig(match || null);
        }
      })
      .catch(() => {});
  }, [open, hotelId, config]);

  // Build 3D scene
  useEffect(() => {
    if (!open || !containerRef.current) return;

    // Small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      const container = containerRef.current;
      if (!container) return;

      if (cleanupRef.current) cleanupRef.current();

      const w = container.clientWidth;
      const h = container.clientHeight;
      if (w === 0 || h === 0) return;

      const scene = new THREE.Scene();
      scene.background = new THREE.Color(0x0a0e1a);

      const camera = new THREE.PerspectiveCamera(50, w / h, 0.1, 100);
      camera.position.set(4, 3, 6);

      const renderer = new THREE.WebGLRenderer({ antialias: true });
      renderer.setSize(w, h);
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      renderer.toneMapping = THREE.ACESFilmicToneMapping;
      renderer.toneMappingExposure = 1.2;
      renderer.shadowMap.enabled = true;
      container.appendChild(renderer.domElement);

      const controls = new OrbitControls(camera, renderer.domElement);
      controls.target.set(0, 1.2, 0);
      controls.minDistance = 2;
      controls.maxDistance = config.size.w * 1.5;
      controls.maxPolarAngle = Math.PI * 0.85;
      controls.minPolarAngle = 0.1;
      controls.enablePan = false;
      controls.enableDamping = true;
      controls.dampingFactor = 0.05;
      controls.update();

      // Build the room (AI config or template)
      if (aiConfig) {
        import('@/lib/buildRoomFromAI').then(({ buildRoomFromAIConfig }) => {
          buildRoomFromAIConfig(scene, aiConfig);
        }).catch(() => buildRoom(scene, config));
      } else {
        buildRoom(scene, config);
      }

      // Add floating availability indicator above bed
      const addStatusIndicator = (avail) => {
        // Remove previous indicator
        if (statusSpriteRef.current && scene.getObjectByName('statusGroup')) {
          scene.remove(scene.getObjectByName('statusGroup'));
        }

        const group = new THREE.Group();
        group.name = 'statusGroup';
        const { h, d } = config.size;
        group.position.set(0, h - 0.3, -d / 2 + 1.5);

        // Glowing orb
        const isAvailable = avail && avail.available > 0;
        const orbColor = isAvailable ? 0x22c55e : 0xef4444;
        const orbGeo = new THREE.SphereGeometry(0.08, 16, 16);
        const orbMat = new THREE.MeshStandardMaterial({
          color: orbColor, emissive: orbColor, emissiveIntensity: 0.8,
          transparent: true, opacity: 0.9
        });
        const orb = new THREE.Mesh(orbGeo, orbMat);
        group.add(orb);

        // Glow ring
        const ringGeo = new THREE.RingGeometry(0.12, 0.18, 32);
        const ringMat = new THREE.MeshStandardMaterial({
          color: orbColor, emissive: orbColor, emissiveIntensity: 0.5,
          transparent: true, opacity: 0.4, side: THREE.DoubleSide
        });
        const ring = new THREE.Mesh(ringGeo, ringMat);
        group.add(ring);

        // Point light for glow effect
        const glow = new THREE.PointLight(orbColor, 0.6, 2);
        group.add(glow);

        scene.add(group);
        statusSpriteRef.current = group;

        // Animate: gentle bob + ring rotation
        let bobTime = 0;
        const animateStatus = () => {
          bobTime += 0.02;
          group.position.y = h - 0.3 + Math.sin(bobTime) * 0.05;
          ring.rotation.z += 0.01;
          ring.rotation.x = Math.sin(bobTime * 0.5) * 0.3;
        };
        return animateStatus;
      };

      let statusAnim = null;
      if (availability) {
        statusAnim = addStatusIndicator(availability);
      }

      // Add bounty markers for active bounties in this room
      const bountyAnims = [];
      const roomBounties = bounties.filter(b => {
        // Match bounties that might be for rooms matching current config
        const rn = b.room_number || '';
        return rn && b.status === 'open';
      }).slice(0, 5); // Max 5 markers

      roomBounties.forEach((bounty, idx) => {
        const pos = bounty.position_3d || { x: 0, y: 1.5, z: 0 };
        const markerGroup = new THREE.Group();
        markerGroup.name = `bounty-${bounty.id}`;

        // Offset markers so they don't overlap
        const offsetX = (idx - Math.floor(roomBounties.length / 2)) * 0.8;
        markerGroup.position.set(pos.x + offsetX, pos.y, pos.z);

        // Diamond shape marker
        const diamondGeo = new THREE.OctahedronGeometry(0.12, 0);
        const diamondColor = bounty.priority === 'urgent' ? 0xff4444 :
                            bounty.priority === 'high' ? 0xff8800 :
                            0x22d3ee; // cyan for medium/low
        const diamondMat = new THREE.MeshStandardMaterial({
          color: diamondColor, emissive: diamondColor, emissiveIntensity: 0.7,
          transparent: true, opacity: 0.85,
        });
        const diamond = new THREE.Mesh(diamondGeo, diamondMat);
        markerGroup.add(diamond);

        // Outer glow ring
        const glowRing = new THREE.Mesh(
          new THREE.TorusGeometry(0.2, 0.02, 8, 24),
          new THREE.MeshStandardMaterial({
            color: diamondColor, emissive: diamondColor, emissiveIntensity: 0.5,
            transparent: true, opacity: 0.3, side: THREE.DoubleSide,
          })
        );
        glowRing.rotation.x = Math.PI / 2;
        markerGroup.add(glowRing);

        // Point light for glow
        const light = new THREE.PointLight(diamondColor, 0.4, 1.5);
        markerGroup.add(light);

        // Vertical beam (thin line going up)
        const beamGeo = new THREE.CylinderGeometry(0.005, 0.005, 0.6, 4);
        const beamMat = new THREE.MeshStandardMaterial({
          color: diamondColor, emissive: diamondColor, emissiveIntensity: 0.8,
          transparent: true, opacity: 0.3,
        });
        const beam = new THREE.Mesh(beamGeo, beamMat);
        beam.position.y = 0.4;
        markerGroup.add(beam);

        scene.add(markerGroup);

        // Animate: spin + bob
        let t = idx * 1.5; // Offset animation phase per marker
        bountyAnims.push(() => {
          t += 0.025;
          diamond.rotation.y += 0.02;
          markerGroup.position.y = pos.y + Math.sin(t) * 0.06;
          glowRing.rotation.z += 0.015;
          // Pulse opacity
          diamondMat.opacity = 0.7 + Math.sin(t * 2) * 0.15;
        });
      });

      let animId;
      const animate = () => {
        animId = requestAnimationFrame(animate);
        controls.update();
        if (statusAnim) statusAnim();
        bountyAnims.forEach(fn => fn());
        renderer.render(scene, camera);
      };
      animate();

      const onResize = () => {
        const ww = container.clientWidth;
        const hh = container.clientHeight;
        if (ww > 0 && hh > 0) {
          camera.aspect = ww / hh;
          camera.updateProjectionMatrix();
          renderer.setSize(ww, hh);
        }
      };
      window.addEventListener('resize', onResize);

      cleanupRef.current = () => {
        window.removeEventListener('resize', onResize);
        if (animId) cancelAnimationFrame(animId);
        controls.dispose();
        renderer.dispose();
        if (container.contains(renderer.domElement)) container.removeChild(renderer.domElement);
        cleanupRef.current = null;
      };
    }, 100);

    return () => {
      clearTimeout(timer);
      if (cleanupRef.current) cleanupRef.current();
    };
  }, [open, selectedIdx, config, aiConfig, availability, bounties]);

  // Lock body scroll when open
  useEffect(() => {
    if (open) document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  // ESC to close
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/95 backdrop-blur-sm flex flex-col" data-testid="room-3d-viewer-modal">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-[#0a0e1a]/90 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-blue-500/15 border border-blue-500/30 flex items-center justify-center">
            <Box className="w-4 h-4 text-blue-400" />
          </div>
          <div>
            <h2 className="text-sm font-bold text-white">{config.name}</h2>
            <p className="text-[10px] text-white/50">
              {config.bedType} | {config.sqft} sq ft | Up to {config.maxGuests} guests
            </p>
          </div>
          <Badge className="text-[8px] bg-blue-500/10 text-blue-300 border-blue-500/20">3D Preview</Badge>
          {bounties.filter(b => b.status === 'open').length > 0 && (
            <Badge className="text-[8px] bg-amber-500/10 text-amber-400 border-amber-500/20 animate-pulse" data-testid="bounty-count-badge">
              {bounties.filter(b => b.status === 'open').length} Active Bounties
            </Badge>
          )}
          {aiConfig && (
            <Badge className="text-[8px] bg-emerald-500/10 text-emerald-300 border-emerald-500/20">AI Generated</Badge>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* Room type switcher */}
          <div className="flex gap-1 bg-white/5 rounded-lg p-0.5">
            {ROOM_CONFIGS.map((rc, i) => (
              <button
                key={rc.id}
                onClick={() => setSelectedIdx(i)}
                className={cn(
                  "px-3 py-1 rounded-md text-[10px] font-medium transition-all",
                  i === selectedIdx
                    ? "bg-blue-500/20 text-blue-300"
                    : "text-white/40 hover:text-white/70"
                )}
                data-testid={`room-3d-tab-${rc.id}`}
              >
                {rc.name}
              </button>
            ))}
          </div>

          <Button
            size="sm"
            variant="ghost"
            onClick={onClose}
            className="text-white/60 hover:text-white h-8 w-8 p-0"
            data-testid="room-3d-close-btn"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>
      </div>

      {/* 3D Viewport */}
      <div className="flex-1 relative">
        <div ref={containerRef} className="absolute inset-0" data-testid="room-3d-canvas" />

        {/* Controls hint + Book CTA */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-3">
          <div className="flex items-center gap-4 bg-black/60 backdrop-blur-md rounded-full px-4 py-2 text-white/50 text-[10px]">
            <span className="flex items-center gap-1"><RotateCcw className="w-3 h-3" /> Drag to rotate</span>
            <span className="w-px h-3 bg-white/20" />
            <span>Scroll to zoom</span>
            <span className="w-px h-3 bg-white/20" />
            <span>ESC to close</span>
          </div>

          {availability && availability.available > 0 && onBook && (
            <button
              onClick={() => onBook(config.name, {
                bedType: config.bedType,
                sqft: config.sqft,
                maxGuests: config.maxGuests,
                amenities: config.amenities,
                price: config.price,
              }, availability)}
              className="flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-full px-5 py-2.5 text-sm font-semibold transition-all hover:scale-105 shadow-lg shadow-emerald-500/30"
              data-testid="room-3d-book-btn"
            >
              <ShoppingBag className="w-4 h-4" />
              Book This Room
              <span className="text-[10px] opacity-80 font-normal ml-1">
                from ${config.price}/night
              </span>
            </button>
          )}

          {availability && availability.available === 0 && (
            <div className="flex items-center gap-2 bg-red-500/20 border border-red-500/30 text-red-300 rounded-full px-5 py-2.5 text-sm font-medium" data-testid="room-3d-sold-out">
              Fully Booked
            </div>
          )}
        </div>

        {/* Room info overlay */}
        <div className="absolute top-4 left-4 bg-black/60 backdrop-blur-md rounded-xl p-3 max-w-[200px]">
          <p className="text-white text-xs font-semibold mb-2">{config.name}</p>
          <div className="space-y-1.5">
            <div className="flex items-center gap-2 text-white/70 text-[10px]">
              <Bed className="w-3 h-3" /> {config.bedType}
            </div>
            <div className="flex items-center gap-2 text-white/70 text-[10px]">
              <Users className="w-3 h-3" /> Up to {config.maxGuests} guests
            </div>
            <div className="flex items-center gap-2 text-white/70 text-[10px]">
              <Maximize className="w-3 h-3" /> {config.sqft} sq ft
            </div>
          </div>
          <div className="flex flex-wrap gap-1 mt-2">
            {config.amenities.slice(0, 5).map((a) => {
              const Icon = AMENITY_ICONS[a];
              return (
                <Badge key={a} className="text-[7px] bg-white/10 text-white/60 border-white/10 px-1.5 py-0.5">
                  {Icon && <Icon className="w-2 h-2 mr-0.5" />} {a}
                </Badge>
              );
            })}
          </div>
        </div>

        {/* Live Availability Overlay */}
        {availability && (
          <div className="absolute top-4 right-4 bg-black/60 backdrop-blur-md rounded-xl p-3 min-w-[180px]" data-testid="room-3d-availability">
            <div className="flex items-center gap-2 mb-2">
              <div className={cn(
                "w-2.5 h-2.5 rounded-full",
                availability.available > 0 ? "bg-emerald-500 animate-pulse" : "bg-red-500"
              )} />
              <p className="text-[10px] font-bold uppercase tracking-wider text-white/70">Live Availability</p>
            </div>

            <div className="space-y-2">
              {/* Available count */}
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-white/60">Available</span>
                <span className={cn(
                  "text-sm font-bold font-mono",
                  availability.available > 0 ? "text-emerald-400" : "text-red-400"
                )}>
                  {availability.available}
                </span>
              </div>

              {/* Occupied count */}
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-white/60">Occupied</span>
                <span className="text-sm font-bold font-mono text-blue-400">{availability.occupied}</span>
              </div>

              {/* Total */}
              <div className="flex items-center justify-between border-t border-white/10 pt-1.5">
                <span className="text-[10px] text-white/60">Total Rooms</span>
                <span className="text-sm font-bold font-mono text-white/80">{availability.total}</span>
              </div>

              {/* Occupancy bar */}
              <div>
                <div className="flex justify-between text-[8px] text-white/40 mb-0.5">
                  <span>Occupancy</span>
                  <span>{availability.total > 0 ? Math.round(availability.occupied / availability.total * 100) : 0}%</span>
                </div>
                <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-1000"
                    style={{
                      width: `${availability.total > 0 ? (availability.occupied / availability.total) * 100 : 0}%`,
                      background: availability.occupied / availability.total > 0.8
                        ? 'linear-gradient(90deg, #ef4444, #f97316)'
                        : availability.occupied / availability.total > 0.5
                          ? 'linear-gradient(90deg, #f59e0b, #eab308)'
                          : 'linear-gradient(90deg, #22c55e, #10b981)'
                    }}
                  />
                </div>
              </div>

              {/* Status message */}
              <p className={cn(
                "text-[9px] font-medium text-center pt-1",
                availability.available > 0 ? "text-emerald-400/80" : "text-red-400/80"
              )}>
                {availability.available > 0
                  ? `${availability.available} room${availability.available > 1 ? 's' : ''} ready to book`
                  : 'All rooms currently occupied'
                }
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
